import React from "react";
import { Link } from "react-router-dom";
import { Building2, Briefcase, Users, Lightbulb, HandHeart, ExternalLink, Calendar, Coffee, Sparkles, MapPin } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { IMAGES } from "@/lib/site";

const missions = [
  { icon: Briefcase, t: "Stages & PFMP", d: "Accompagner le placement des élèves en Périodes de Formation en Milieu Professionnel." },
  { icon: Users, t: "Insertion professionnelle", d: "Faciliter l'accès des jeunes au premier emploi grâce à notre réseau." },
  { icon: Lightbulb, t: "Interventions de pros", d: "Inviter les professionnels en cours pour partager la réalité des métiers." },
  { icon: Sparkles, t: "Projets pédagogiques", d: "Co-construire des projets concrets avec les enseignants et les entreprises." },
  { icon: HandHeart, t: "Partenariats durables", d: "Construire des relations de long terme : alternance, taxe d'apprentissage, mécénat." },
];

const partenaires = [
  {
    nom: "Carrefour Angers",
    secteur: "Grande distribution",
    description: "Accueil régulier de stagiaires en CAP EPC et Bac Pro MCV. Participation au job dating annuel.",
    site: "https://www.carrefour.fr",
  },
  {
    nom: "Mutualité Anjou-Mayenne",
    secteur: "Santé & médico-social",
    description: "Partenaire historique pour les stages des Bac Pro ASSP et AEPA en EHPAD.",
    site: "https://www.mutualite-anjou-mayenne.fr",
  },
  {
    nom: "Crèche Les P'tits Loups",
    secteur: "Petite enfance",
    description: "Accueil des élèves de CAP AEPE pour des immersions et stages d'observation.",
    site: "#",
  },
  {
    nom: "Decathlon Angers",
    secteur: "Sport & vente",
    description: "Stages en magasin et interventions de chefs de rayon pour témoigner de leur métier.",
    site: "https://www.decathlon.fr",
  },
  {
    nom: "Brico Dépôt",
    secteur: "Bricolage & bâtiment",
    description: "Partenaire pour les filières Maintenance des Bâtiments et Logistique.",
    site: "https://www.bricodepot.fr",
  },
  {
    nom: "Ville d'Angers",
    secteur: "Collectivité territoriale",
    description: "Accueil de stagiaires Bac Pro AGORA dans les services administratifs municipaux.",
    site: "https://www.angers.fr",
  },
];

const evenements = [
  {
    titre: "Petits-déjeuners entreprises",
    date: "Plusieurs sessions par an",
    description: "Rencontres conviviales avec nos équipes pédagogiques autour d'un café pour échanger sur nos formations.",
    image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1200&q=80",
  },
  {
    titre: "Soirée des partenaires",
    date: "Juin · annuel",
    description: "La grande soirée annuelle pour remercier nos entreprises partenaires et valoriser les réussites de l'année.",
    image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=1200&q=80",
  },
  {
    titre: "Forum des métiers",
    date: "Janvier",
    description: "Une journée dédiée à la découverte des métiers, où professionnels et élèves échangent autour de stands.",
    image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80",
  },
  {
    titre: "Job dating",
    date: "Avril",
    description: "Une matinée d'entretiens express pour aider nos élèves à décrocher leur stage ou leur contrat d'apprentissage.",
    image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?auto=format&fit=crop&w=1200&q=80",
  },
  {
    titre: "Visites d'entreprises",
    date: "Tout au long de l'année",
    description: "Découverte sur site des process, ateliers et organisations de nos partenaires.",
    image: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1200&q=80",
  },
];

export default function BureauEntreprises() {
  return (
    <div data-testid="bureau-entreprises-page">
      <PageHero
        eyebrow="Espace entreprises · Bureau des entreprises"
        title="Le pont entre le lycée et les acteurs économiques du territoire"
        subtitle="Le Bureau des entreprises est votre interlocuteur unique au lycée Joseph Wresinski. Notre mission : développer des collaborations fortes et durables avec les entreprises pour soutenir la réussite professionnelle de nos élèves."
        image={IMAGES.partenaires}
      >
        <div className="mt-2 flex flex-wrap gap-3">
          <Link
            to="/entreprises/devenir-partenaire"
            data-testid="bureau-cta-partenaire"
            className="inline-flex items-center gap-2 rounded-full bg-brand-sand-dark px-5 py-3 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sand"
          >
            Devenir partenaire
          </Link>
          <Link
            to="/entreprises/taxe-apprentissage"
            data-testid="bureau-cta-taxe"
            className="inline-flex items-center gap-2 rounded-full border border-brand-cream/30 px-5 py-3 text-sm font-semibold text-brand-cream transition-all duration-300 hover:bg-brand-cream hover:text-brand-slate-dark"
          >
            Soutenir le lycée
          </Link>
        </div>
      </PageHero>

      <Section eyebrow="Nos missions" title="Cinq axes de collaboration au service des élèves">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {missions.map((m, i) => (
            <Card key={m.t} data-testid={`bureau-mission-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-slate/10 text-brand-slate-dark">
                <m.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">{m.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section wrapper="bg-brand-sand-wrapper/40" eyebrow="Nos partenaires" title="Ils nous font confiance">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {partenaires.map((p, i) => (
            <Card key={p.nom} data-testid={`partenaire-${i}`} className="flex flex-col">
              <div className="flex items-center gap-4">
                <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-brand-slate text-brand-cream font-heading text-lg font-bold">
                  {p.nom.charAt(0)}
                </div>
                <div>
                  <p className="font-heading text-base font-bold leading-tight text-brand-slate-dark">
                    {p.nom}
                  </p>
                  <p className="text-xs font-medium uppercase tracking-wider text-brand-sand-dark">
                    {p.secteur}
                  </p>
                </div>
              </div>
              <p className="mt-5 flex-1 text-sm leading-relaxed text-muted-foreground">
                {p.description}
              </p>
              {p.site && p.site !== "#" && (
                <a
                  href={p.site}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`partenaire-site-${i}`}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-slate transition-colors hover:text-brand-sand-dark"
                >
                  Site web <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </Card>
          ))}
        </div>
        <p className="mt-8 text-sm text-muted-foreground">
          Vous souhaitez figurer parmi nos partenaires ?{" "}
          <Link to="/entreprises/devenir-partenaire" className="font-semibold text-brand-slate underline-offset-4 hover:underline">
            Rejoignez-nous
          </Link>
          .
        </p>
      </Section>

      <Section eyebrow="Événements entreprises" title="Des temps forts pour se rencontrer">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {evenements.map((e, i) => (
            <div
              key={e.titre}
              data-testid={`evenement-${i}`}
              className="group flex flex-col overflow-hidden rounded-3xl border border-black/5 bg-brand-cream transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={e.image}
                  alt={e.titre}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-brand-cream/95 px-3 py-1 text-xs font-semibold text-brand-slate-dark backdrop-blur">
                  <Calendar className="h-3 w-3" /> {e.date}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-heading text-lg font-bold text-brand-slate-dark">{e.titre}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{e.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section wrapper="bg-brand-slate-dark text-brand-cream">
        <div className="grid items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <Coffee className="h-8 w-8 text-brand-sand" />
            <h2 className="mt-4 font-heading text-3xl font-bold sm:text-4xl">
              Échangeons autour d'un café
            </h2>
            <p className="mt-4 max-w-xl text-brand-cream/85">
              Notre Bureau des entreprises est à votre écoute pour étudier toute proposition de
              collaboration et imaginer ensemble une relation gagnant-gagnant.
            </p>
            <p className="mt-4 flex items-center gap-2 text-sm text-brand-cream/70">
              <MapPin className="h-4 w-4 text-brand-sand" /> Angers (49) — sur rendez-vous
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link
              to="/entreprises/devenir-partenaire"
              data-testid="bureau-cta-bottom"
              className="inline-flex items-center gap-2 rounded-full bg-brand-sand-dark px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sand"
            >
              Devenir partenaire
            </Link>
            <Link
              to="/contact"
              data-testid="bureau-cta-contact"
              className="inline-flex items-center gap-2 rounded-full border border-brand-cream/30 px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:bg-brand-cream hover:text-brand-slate-dark"
            >
              Nous contacter
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
